import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { fetchAccountLedger } from "@/shared/api/accounting";
import { docLink } from "@/shared/doc-link";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

/** Format YYYY-MM-DD as local calendar day (no UTC shift). */
function formatEntryDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd || "—";
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return new Date(y, mo - 1, d).toLocaleDateString();
}

export function AccountInquiryPage() {
  const { companySlug, accountId } = useParams();

  const ledgerQuery = useQuery({
    queryKey: queryKeys.gl.accountLedger(
      companySlug ?? "",
      accountId ?? "",
    ),
    enabled: Boolean(companySlug && accountId),
    queryFn: () => fetchAccountLedger(companySlug!, accountId!),
  });
  const errorMsg = queryErrorMessage(ledgerQuery.error, ledgerQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug || !accountId) return null;

  const data = ledgerQuery.data ?? null;
  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  return (
    <PageFrame width="narrow">
      {ledgerQuery.isPending || !data ? (
        <div>
          <PageHeader
            eyebrow={
              <Link
                to={`/c/${companySlug}/gl/trial-balance`}
                className="text-inherit no-underline hover:underline"
              >
                Trial balance
              </Link>
            }
            title="Account"
          />
          <div className="mt-6">
            {displayError ? (
              <Alert variant="destructive">{displayError}</Alert>
            ) : (
              <Alert variant="muted">Loading account…</Alert>
            )}
          </div>
        </div>
      ) : (
        <>
          <PageHeader
            eyebrow={
              <Link
                to={`/c/${companySlug}/gl/trial-balance`}
                className="text-inherit no-underline hover:underline"
              >
                Trial balance
              </Link>
            }
            title={`${data.account.code} ${data.account.name}`}
            description={`${data.account.type} · balance ${data.currencyCode} ${moneyDisplay(data.balance)}`}
          />
          {displayError && (
            <Alert variant="destructive" className="mt-4">
              {displayError}
            </Alert>
          )}
          {data.entries.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                title="No posted lines yet"
                description="Activity hits this account when you sell, buy, or post journals."
                action={
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/c/${companySlug}/gl/trial-balance`}
                      className="no-underline"
                    >
                      Back to trial balance
                    </Link>
                  </Button>
                }
              />
            </div>
          ) : (
            <table className="mt-8 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wider text-[var(--color-muted)]">
                  <th className="py-2 pr-2 font-semibold">Date</th>
                  <th className="py-2 pr-2 font-semibold">Entry</th>
                  <th className="py-2 pr-2 text-right font-semibold">Debit</th>
                  <th className="py-2 pr-2 text-right font-semibold">Credit</th>
                  <th className="py-2 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => {
                  const sourceHref = docLink(
                    companySlug,
                    e.sourceDocType,
                    e.sourceDocId,
                  );
                  return (
                    <tr
                      key={e.lineId}
                      className="border-b border-[var(--color-border)]/60 align-top"
                    >
                      <td className="py-2 pr-2 text-xs text-[var(--color-muted)]">
                        {formatEntryDate(e.entryDate)}
                      </td>
                      <td className="py-2 pr-2">
                        <div className="font-mono text-xs">
                          <Link
                            to={`/c/${companySlug}/gl/journals/${e.journalId}`}
                            className="no-underline"
                          >
                            {e.journalNumber}
                          </Link>
                        </div>
                        <div className="text-[var(--color-muted)]">
                          {e.memo || e.kind}
                          {e.sourceDocType ? (
                            sourceHref ? (
                              <>
                                {" · "}
                                <Link to={sourceHref} className="no-underline">
                                  {e.sourceDocType}
                                </Link>
                              </>
                            ) : (
                              ` · ${e.sourceDocType}`
                            )
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-right font-mono">
                        {Number(e.debit) ? moneyDisplay(e.debit) : "—"}
                      </td>
                      <td className="py-2 pr-2 text-right font-mono">
                        {Number(e.credit) ? moneyDisplay(e.credit) : "—"}
                      </td>
                      <td className="py-2 text-right font-mono">
                        {moneyDisplay(e.balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-3 pr-2" colSpan={2}>
                    Total
                  </td>
                  <td className="py-3 pr-2 text-right font-mono">
                    {moneyDisplay(data.totalDebit)}
                  </td>
                  <td className="py-3 pr-2 text-right font-mono">
                    {moneyDisplay(data.totalCredit)}
                  </td>
                  <td className="py-3 text-right font-mono">
                    {moneyDisplay(data.balance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </>
      )}
    </PageFrame>
  );
}
